import { createApi } from '@reduxjs/toolkit/query/react';

import sleep from 'sleep-promise';

import { ADDRESS_UPDATION_WAIT_TIME } from '@/config/constants';
import { IS_PROD } from '@/config/env';
import { Address } from '@/types/Address';
import {
  AddToCart,
  EnhancedShoppingCartResponse,
  PaymentAuthenticationResponse,
  ShoppingCartResponse,
} from '@/types/Cart';
import { CheckoutFormData } from '@/types/CheckoutFormData';
import { ResponseError } from '@/types/Error';
import { DeliveryFrequencyPayload, RemoveFoodLabelPayload } from '@/types/EzShip';
import { ApplyFoodLabelPayload } from '@/types/FoodLabel';
import { GiftCardPayload } from '@/types/GiftCard';
import { EnhancedOrderSummaryResponse, OrderSummaryResponse } from '@/types/OrderSummary';
import { Payment } from '@/types/Payment';
import { AddCreditCardPayload } from '@/types/PaymentCard';
import { SuccessResponse } from '@/types/Success';

import { orderSummaryAdapter, shoppingCartAdapter } from '../adapters';
import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';
// import { customerApi } from './customer';
import { ezShipApi } from './ezShip';
import { foodLabelsApi } from './foodLabels';

const contentTypes = {
  applicationJson: 'application/json',
};

export const shoppingCartApi = createApi({
  reducerPath: 'shoppingCartApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `Shopping` }),
  tagTypes: ['ShoppingCart', 'OrderSummary', 'PaymentAuthenticationKey', 'OrderSubmissionStatus', 'PaymentsDetails'],
  endpoints: builder => ({
    getShoppingCart: builder.query<EnhancedShoppingCartResponse, void>({
      query: () => ({
        url: '/GetCartDetail',
        next: { tags: ['ShoppingCart'] },
      }),
      transformResponse: (response: ShoppingCartResponse, meta, arg) => shoppingCartAdapter(response),
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['ShoppingCart'],
    }),
    getOrderSummary: builder.query<EnhancedOrderSummaryResponse, void>({
      query: () => ({
        url: '/GetOrderSummary',
        next: { tags: ['OrderSummary'] },
      }),
      transformResponse: (response: OrderSummaryResponse, meta, arg) => orderSummaryAdapter(response),
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['OrderSummary'],
    }),
    checkOrderSubmissionStatus: builder.query<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/CheckOrderSubmissionStatus',
        next: { tags: ['OrderSubmissionStatus'] },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['OrderSubmissionStatus'],
    }),
    getPaymentAuthenticationKey: builder.query<PaymentAuthenticationResponse, void>({
      query: () => ({
        url: '/GetPaymentAuthenticationKey',
        next: { tags: ['PaymentAuthenticationKey'] },
      }),
      transformResponse: (response: PaymentAuthenticationResponse, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['PaymentAuthenticationKey'],
    }),

    addToCart: builder.mutation<SuccessResponse<unknown>, AddToCart>({
      query: newCartItem => ({
        url: '/AddItemToCart',
        method: 'POST',
        body: JSON.stringify(newCartItem),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    addListToCart: builder.mutation<SuccessResponse<unknown>, AddToCart[]>({
      query: newCartItem => ({
        url: '/AddItemsToCart',
        method: 'POST',
        body: JSON.stringify(newCartItem),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    addToCartUsingPawPoints: builder.mutation<SuccessResponse<unknown>, AddToCart>({
      query: newCartItem => ({
        url: '/UsePawPoints',
        method: 'POST',
        body: JSON.stringify(newCartItem),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    addToCartUsingSampleCredits: builder.mutation<SuccessResponse<unknown>, AddToCart>({
      query: newCartItem => ({
        url: '/UseSampleCredits',
        method: 'POST',
        body: JSON.stringify(newCartItem),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),

    updateQuantity: builder.mutation<SuccessResponse<unknown>, { id: string; quantity: number }>({
      query: ({ id, quantity }) => ({
        url: `/UpdateItemQuantity/?id=${id}&quantity=${quantity}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    updateEZShip: builder.mutation<SuccessResponse<unknown>, { id: string; type: number }>({
      query: ({ id, type }) => ({
        url: `/UpdateItemType/?id=${id}&type=${type}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    updateListEZShip: builder.mutation<SuccessResponse<unknown>, { ids: string[]; type: number }>({
      query: data => ({
        url: `/UpdateItemTypes`,
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),
    removeFromCart: builder.mutation<SuccessResponse<unknown>, { id: string; removeForcefully?: boolean }>({
      query: ({ id, removeForcefully }) => ({
        url: `/RemoveItemFromCart/?id=${id}${removeForcefully ? '&removeForcefully=true' : ''}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart', 'OrderSummary'] : []),
    }),
    removeListFromCart: builder.mutation<SuccessResponse<unknown>, { ids: string[]; removeForcefully?: boolean }>({
      query: ({ ids, removeForcefully }) => ({
        url: `/RemoveItemsFromCart${removeForcefully ? '?removeForcefully=true' : ''}`,
        method: 'POST',
        body: ids,
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart', 'OrderSummary'] : []),
    }),
    applyFoodLabel: builder.mutation<SuccessResponse<unknown>, ApplyFoodLabelPayload>({
      query: payload => ({
        url: '/SavePetFoodLabel',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    saveEzShipTitle: builder.mutation<SuccessResponse<unknown>, { title: string }>({
      query: payload => ({
        url: '/SaveEzShipTitle',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    applyCoupon: builder.mutation<SuccessResponse<unknown>, { couponCode: string }>({
      query: data => ({
        url: `/UseCouponCode`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    applyGiftCard: builder.mutation<SuccessResponse<unknown>, GiftCardPayload>({
      query: data => ({
        url: `/UseGiftCard`,
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    removeCoupon: builder.mutation<SuccessResponse<unknown>, undefined>({
      query: coupon => ({ url: `/RemoveCouponCode`, method: 'POST', body: JSON.stringify({ coupon }) }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    removeGiftCard: builder.mutation<SuccessResponse<unknown>, undefined>({
      query: payload => ({ url: `/RemoveGiftCard`, method: 'POST', body: JSON.stringify(payload) }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    addShippingAddress: builder.mutation<SuccessResponse<unknown>, Address>({
      queryFn: async (payload, api, extraOptions, baseQuery) => {
        const resp = await baseQuery({
          url: `/UseShippingAddress${payload?.updatePhone ? '?updatePhone=true' : ''}`,
          method: 'POST',
          body: payload,
          headers: {
            'Content-Type': contentTypes.applicationJson,
          },
        });

        if (!IS_PROD) await sleep(ADDRESS_UPDATION_WAIT_TIME);

        if (resp.error) return { error: resp.error };

        return resp as any;
      },

      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),

      // async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      //   const { data } = await queryFulfilled;

      //   if (arg.updatePhone && data.success) dispatch(customerApi.util.invalidateTags(['Customers']));
      // },
    }),
    selectShippingAddress: builder.mutation<SuccessResponse<unknown>, number | string>({
      query: addressType => ({
        url: `/UseShippingAddressOnFile/?addressType=${addressType}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    addCreditCard: builder.mutation<
      SuccessResponse<unknown>,
      { payload: AddCreditCardPayload; billingSameAsShipping: boolean; autoOrderId?: string | number }
    >({
      query: ({ payload, billingSameAsShipping, autoOrderId }) => ({
        url: `/UseCreditCard/?billingSameAsShipping=${billingSameAsShipping}${
          autoOrderId ? `&autoOrderId=${autoOrderId}` : ''
        }`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary', 'PaymentsDetails'] : []),
    }),
    selectCreditCard: builder.mutation<SuccessResponse<unknown>, number | string>({
      query: creditCardType => ({
        url: `/UseCreditCardOnFile/?type=${creditCardType}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    selectHyperWallet: builder.mutation<SuccessResponse<unknown>, number | string>({
      query: walletAccountType => ({
        url: `/UseHyperWallet/?walletAccountType=${walletAccountType}`,
        method: 'POST',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    updateDeliveryFrequency: builder.mutation<SuccessResponse<unknown>, DeliveryFrequencyPayload>({
      query: payload => ({
        url: `/UpdateDeliveryFrequency`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['OrderSummary'] : []),
    }),
    placeOrder: builder.mutation<SuccessResponse<unknown>, CheckoutFormData>({
      query: payload => ({
        url: `/SubmitCheckout`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    orderComplete: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: 'OrderComplete',
        method: 'POST',
        // body: JSON.stringify({}),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart'] : []),
    }),

    getAllPaymentMethods: builder.query<Payment[], void>({
      query: payload => ({
        url: `/GetPaymentMethods`,
        method: 'GET',
      }),
      providesTags: ['PaymentsDetails'],
      transformResponse: (response: SuccessResponse<Payment[]>, meta, arg) => response.data,
      transformErrorResponse: response => response as ResponseError,
    }),

    removeEzShipFoodLabel: builder.mutation<SuccessResponse<unknown>, RemoveFoodLabelPayload>({
      query: payload => ({
        url: `/RemovePetFoodLabel`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(foodLabelsApi.util.invalidateTags(['foodLabels']));
        dispatch(ezShipApi.util.invalidateTags(['EzShipCartItems']));
      },
    }),
    removeDiscounts: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: `/removediscounts`,
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['ShoppingCart', 'OrderSummary'] : []),
    }),
    setShoppingCartType: builder.mutation<SuccessResponse<unknown>, number>({
      query: shoppingFlowType => ({
        url: `/SetShoppingCartType?ShoppingFlowType=${shoppingFlowType}`,
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetShoppingCartQuery,
  useAddToCartMutation,
  useAddToCartUsingPawPointsMutation,
  useAddToCartUsingSampleCreditsMutation,
  useUpdateEZShipMutation,
  useUpdateQuantityMutation,
  useRemoveFromCartMutation,
  useApplyCouponMutation,
  useApplyGiftCardMutation,
  useRemoveCouponMutation,
  useRemoveGiftCardMutation,
  useApplyFoodLabelMutation,
  useSaveEzShipTitleMutation,
  useGetOrderSummaryQuery,
  useAddShippingAddressMutation,
  useGetPaymentAuthenticationKeyQuery,
  useSelectShippingAddressMutation,
  useUpdateDeliveryFrequencyMutation,
  useSelectCreditCardMutation,
  useSelectHyperWalletMutation,
  usePlaceOrderMutation,
  useAddCreditCardMutation,
  useCheckOrderSubmissionStatusQuery,
  useGetAllPaymentMethodsQuery,
  useRemoveEzShipFoodLabelMutation,
  useOrderCompleteMutation,
  useAddListToCartMutation,
  useRemoveListFromCartMutation,
  useUpdateListEZShipMutation,
  useRemoveDiscountsMutation,
  useSetShoppingCartTypeMutation,
} = shoppingCartApi;
